import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Header.css";

import SideMenu from "./SideMenu";
import Backdrop from "./Backdrop";

import { useDispatch, useSelector } from "react-redux";

import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import axios from "axios";
import getUserData from "../../utils/userDataService.js";

const normalize = (s = "") =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const Header = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [sideMenu, setSideMenu] = useState(false);
  const [q, setQ] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [openSug, setOpenSug] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  useEffect(() => {
    getUserData(dispatch, true);
  }, [dispatch]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await axios.get("/api/product/all-products");
        const raw = res?.data;
        const list =
          Array.isArray(raw?.data) ? raw.data :
          Array.isArray(raw?.products) ? raw.products :
          Array.isArray(raw) ? raw : [];

        if (alive) setAllProducts(list);
      } catch {
        if (alive) setAllProducts([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      const inDesktop = desktopSearchRef.current?.contains(e.target);
      const inMobile = mobileSearchRef.current?.contains(e.target);

      if (!inDesktop && !inMobile) {
        setOpenSug(false);
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, []);

  const accountPath = user?.isAdmin ? "/admin-dashboard" : user ? "/user-dashboard" : "/login";

  const navItems = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Games", path: "/games" },
      { label: "Leaderboard", path: "/leaderboard" },
      { label: "Check Region", path: "/name-check" },
      ...(user ? [{ label: "Subscription", path: "/subscription" }] : []),
      { label: user ? "Dashboard" : "My Account", path: accountPath },
    ],
    [accountPath, user]
  );

  const displayName = useMemo(() => {
    if (!user) return "";
    const name = String(user?.fname || "").trim();
    if (name) return name;
    const email = String(user?.email || "").trim();
    return email.includes("@") ? email.split("@")[0] : email || "Account";
  }, [user]);

  const avatar = useMemo(() => String(user?.picture || "").trim() || "", [user]);

  const balance = useMemo(() => {
    const b = user?.balance;
    const num = typeof b === "number" ? b : typeof b === "string" ? Number(b) : 0;
    return Number.isFinite(num) && num >= 0 ? num : 0;
  }, [user]);

  const balanceText = useMemo(
    () => balance.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    [balance]
  );

  const suggestions = useMemo(() => {
    const s = normalize(q);
    if (!s) return [];
    return allProducts.filter((p) => normalize(p?.name).includes(s)).slice(0, 8);
  }, [q, allProducts]);

  const mobileProductResults = useMemo(() => {
    const s = normalize(q);
    const list = Array.isArray(allProducts) ? allProducts : [];
    if (!s) return list;

    return list.filter((p) => {
      const text = [
        p?.name,
        p?.category,
        p?.tag,
        p?.gameName,
      ].map(normalize).join(" ");

      return text.includes(s);
    });
  }, [q, allProducts]);

  const productImgUrl = (p) => {
    if (!p) return "";
    const v = String(p).replace(/\\/g, "/").trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    if (v.includes(":/")) return `/productImages/${v.split("/").pop()}`;
    if (v.startsWith("/")) return v;
    return `/${v}`;
  };

  const isRouteActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path;
  };

  const goToProduct = (p) => {
    setOpenSug(false);
    setSearchOpen(false);
    setQ("");

    const productName = p?.name || p?._id || p?.id;
    if (productName) navigate(`/product/${encodeURIComponent(productName)}`);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const s = q.trim();
    if (!s) return;

    const exact = allProducts.find((p) => normalize(p?.name) === normalize(s));
    if (exact) return goToProduct(exact);

    navigate("/games");
    setOpenSug(false);
    setSearchOpen(false);
  };

  const onAccountClick = () => {
    navigate(user ? "/user-dashboard" : "/login");
  };

  const onWalletClick = () => navigate("/add-balance");
  const onMobileRightClick = () => navigate(user ? "/add-balance" : "/login");

  const renderSearch = (mobile = false) => (
    <>
      <form
        className={`sgSearchForm ${mobile ? "sgSearchFormMobile" : ""}`}
        onSubmit={onSearchSubmit}
        role="search"
      >
        <input
          className="sgSearchInput"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpenSug(Boolean(e.target.value.trim()));
          }}
          onFocus={() => setOpenSug(Boolean(q.trim()))}
          placeholder="Search games, topups..."
          aria-label="Search games and topups"
        />
        <button className="sgSearchSubmit" type="submit" aria-label="Submit search">
          <SearchIcon />
        </button>
      </form>

      {openSug && suggestions.length > 0 && (
        <div className="sgSug">
          {suggestions.map((p) => (
            <button
              key={p?._id || p?.id || p?.name}
              type="button"
              className="sgSugItem"
              onClick={() => goToProduct(p)}
            >
              <span className="sgSugName">{p?.name}</span>
              <span className="sgSugMeta">Open top-up</span>
            </button>
          ))}
        </div>
      )}
    </>
  );

  const renderMobileSearchPopup = () => (
    <div className="sgMobileSearchOverlay d-block d-lg-none">
      <div className="sgMobileSearchCard" ref={mobileSearchRef}>
        <div className="sgMobileSearchHead">
          <div>
            <h3>Search Products</h3>
            <p>
              {q.trim()
                ? `${mobileProductResults.length} matching product${mobileProductResults.length === 1 ? "" : "s"}`
                : `${allProducts.length} available product${allProducts.length === 1 ? "" : "s"}`}
            </p>
          </div>

          <button
            type="button"
            className="sgMobileSearchClose"
            onClick={() => {
              setSearchOpen(false);
              setOpenSug(false);
            }}
            aria-label="Close search"
          >
            <CloseIcon />
          </button>
        </div>

        <form
          className="sgMobileSearchBox"
          onSubmit={(e) => {
            e.preventDefault();
            if (mobileProductResults[0]) goToProduct(mobileProductResults[0]);
          }}
        >
          <SearchIcon />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Type game name, category, tag..."
            aria-label="Search products"
          />
        </form>

        <div className="sgMobileProductList">
          {mobileProductResults.length > 0 ? (
            mobileProductResults.map((p) => (
              <button
                type="button"
                className="sgMobileProductItem"
                key={p?._id || p?.id || p?.name}
                onClick={() => goToProduct(p)}
              >
                <span className="sgMobileProductImg">
                  {p?.image ? (
                    <img src={productImgUrl(p.image)} alt={p?.name || "product"} />
                  ) : (
                    <SportsEsportsIcon />
                  )}
                </span>
                <span className="sgMobileProductInfo">
                  <strong>{p?.name || "Product"}</strong>
                  <small>{p?.category || p?.tag || "Top Up"}</small>
                </span>
                <span className="sgMobileProductOpen">Open</span>
              </button>
            ))
          ) : (
            <div className="sgMobileSearchEmpty">
              No matching product found
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <header className="sgHeader">
      <div className="sgMain">
        <div className="sgContainer sgMainInner">
          <div className="sgLeft">
            <Link to="/" className="sgBrand" aria-label="Go to home">
              <span className="sgBrandMark">LS</span>
              <span className="sgBrandCopy">
                <span className="sgBrandName">LIMPOOK</span>
                <span className="sgBrandSub">Official Store</span>
              </span>
            </Link>

            <SideMenu sideMenu={sideMenu} setSideMenu={setSideMenu} />
            <Backdrop sideMenu={sideMenu} setSideMenu={setSideMenu} />
          </div>

          <nav className="sgNav d-none d-lg-flex" aria-label="Primary navigation">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className={`sgNavLink ${isRouteActive(item.path) ? "active" : ""}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="sgActions">
            {user && (
              <button
                type="button"
                className="sgBalancePill d-none d-lg-flex"
                onClick={onWalletClick}
                aria-label="Wallet balance"
              >
                <span className="sgActionIcon">
                  <CurrencyRupeeIcon />
                </span>
                <span className="sgActionText">{balanceText}</span>
              </button>
            )}

            {user && (
              <button
                type="button"
                className="sgAddCoinPill d-none d-lg-flex"
                onClick={onWalletClick}
                aria-label="Add coin"
              >
                <span className="sgActionIcon">
                  <AddIcon />
                </span>
                <span className="sgActionText">Add Coin</span>
              </button>
            )}

            <div className={`d-none d-lg-flex ${user ? "sgAccountSlotLogged" : "sgAccountSlotGuest"}`}>
              <button
                type="button"
                className={`sgAccount ${user ? "sgAccountUser" : "sgAccountLogin"}`}
                onClick={onAccountClick}
                aria-label={user ? "Go to dashboard" : "Login"}
                title={user ? displayName : "Login"}
              >
                {avatar ? (
                  <img src={avatar} alt="profile" className="sgAvatar" />
                ) : (
                  <span className="sgAvatarFallback" aria-hidden="true">
                    {user ? <PersonIcon /> : <LoginIcon />}
                  </span>
                )}
                {!user && (
                  <span className="sgAccText">
                    <span className="sgAccName">LOGIN</span>
                  </span>
                )}
              </button>
            </div>

            <div className="sgSearchDock" ref={desktopSearchRef}>
              <button
                type="button"
                className={`sgSearchToggle ${searchOpen ? "active" : ""}`}
                aria-label={searchOpen ? "Close search" : "Open search"}
                aria-expanded={searchOpen}
                onClick={() => {
                  setSearchOpen((p) => !p);
                  setOpenSug(Boolean(q.trim()));
                }}
              >
                {searchOpen ? <CloseIcon /> : <SearchIcon />}
              </button>

              {searchOpen && (
                <div className="sgSearchPanel d-none d-lg-block">
                  {renderSearch()}
                </div>
              )}
            </div>

            <button
              type="button"
              className="sgMobileUserBtn d-flex d-lg-none"
              onClick={onMobileRightClick}
              aria-label={user ? "Open wallet" : "Login"}
            >
              {user ? (
                <>
                  <AccountBalanceWalletIcon className="sgMobBalIcon" />
                  <span className="sgMobBalText">₹{balanceText}</span>
                </>
              ) : (
                <>
                  <LoginIcon className="sgMobBalIcon" />
                  <span className="sgMobBalText">Login</span>
                </>
              )}
            </button>
          </div>
        </div>

        {searchOpen && renderMobileSearchPopup()}
      </div>

      <nav className="sgMobileBottomNav d-flex d-lg-none" aria-label="Mobile quick navigation">
        <Link
          to="/"
          className={`sgBottomNavItem ${isRouteActive("/") ? "active" : ""}`}
          aria-label="Home"
        >
          <span className="sgBottomNavIcon">
            <HomeIcon />
          </span>
          <span className="sgBottomNavText">Home</span>
        </Link>

        <Link
          to="/name-check"
          className={`sgBottomNavItem ${isRouteActive("/name-check") ? "active" : ""}`}
          aria-label="Check region"
        >
          <span className="sgBottomBadge">NEW</span>
          <span className="sgBottomNavIcon">
            <PublicIcon />
          </span>
          <span className="sgBottomNavText">Region</span>
        </Link>

        <button
          type="button"
          className={`sgBottomNavItem ${searchOpen ? "active" : ""}`}
          onClick={() => {
            setSearchOpen((p) => !p);
            setOpenSug(Boolean(q.trim()));
          }}
          aria-label={searchOpen ? "Close search" : "Open search"}
        >
          <span className="sgBottomNavIcon">
            {searchOpen ? <CloseIcon /> : <SearchIcon />}
          </span>
          <span className="sgBottomNavText">Search</span>
        </button>

        <Link
          to="/subscription"
          className={`sgBottomNavItem ${isRouteActive("/subscription") ? "active" : ""}`}
          aria-label="Reseller subscription"
        >
          <span className="sgBottomNavIcon">
            <WorkspacePremiumIcon />
          </span>
          <span className="sgBottomNavText">Reseller</span>
        </Link>

        <button
          type="button"
          className={`sgBottomNavItem ${sideMenu ? "active" : ""}`}
          onClick={() => setSideMenu(true)}
          aria-label="Open menu"
        >
          <span className="sgBottomNavIcon">
            <MenuIcon />
          </span>
          <span className="sgBottomNavText">Menu</span>
        </button>
      </nav>

    </header>
  );
};

export default Header;
